import { InvitationFormHandle } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/InvitationForm";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";
import { ButtonControl } from "../../Buttons/ButtonControl";
import ButtonGroup from "../../Buttons/ButtonGroup";

interface InvitationPopupFooterProps {
    patientContactId: Guid;
    patientContactFacetId: Guid;
    patientPortalSyncState: number;
    accountStatusLocked: boolean;
    unlocking: boolean;
    refusing: boolean;
    resending: boolean;
    createOwn: boolean;
    hasAccount: boolean;
    accountStatusRefused: boolean;
    accountStatusActive: boolean;
    representatives: { contactId: string; permissions: number[] }[];
    setUnlocking: (value: boolean) => void;
    setRefusing: (value: boolean) => void;
    onCancel?: () => void;
    validator: { isValidToSend: boolean; isValidToInvite: boolean };
    formRef: React.RefObject<InvitationFormHandle>;
}

function InvitationPopupFooter(props: Readonly<InvitationPopupFooterProps>): any {
    const { getText } = useTranslation();

    const handleSend = (): void => {
        props.formRef.current.send(props.patientContactFacetId, props.patientContactId);

        props.onCancel();
    };

    const handleInvite = (): void => {
        props.formRef.current
            .invite(props.patientContactId)
            .then(props.onCancel);
    };

    const confirmRefuse = (): void => {
        props.formRef.current.refuse(props.patientContactId);

        props.setRefusing(false);

        props.onCancel();
    };

    if (props.accountStatusLocked && !props.unlocking) {
        return (
            <div className="flex-container flex-end">
                <ButtonGroup>
                    <ButtonControl
                        variant="neutral"
                        onClick={props.onCancel}
                        label={getText("CancelLabel")}
                    />
                    <ButtonControl
                        variant="brand"
                        onClick={() => props.setUnlocking(true)}
                        label={getText("PortalInvitation_NextLabel")}
                    />
                </ButtonGroup>
            </div>
        );
    }

    if (props.refusing) {
        return (
            <div className="flex-container flex-end">
                <ButtonGroup>
                    <ButtonControl
                        variant="neutral"
                        onClick={() => props.setRefusing(false)}
                        label={getText("CancelLabel")}
                    />
                    <ButtonControl
                        variant="brand"
                        onClick={confirmRefuse}
                        label={getText("ConfirmLabel")}
                    />
                </ButtonGroup>
            </div>
        );
    }

    return (
        <div className="flex-container flex-end flex-gap-sm">
            <ButtonControl
                variant="neutral"
                onClick={props.onCancel}
                label={getText("CancelLabel")}
            />

            {(!props.patientPortalSyncState || (!props.accountStatusRefused && !props.accountStatusActive)) && (
                <ButtonControl
                    variant="neutral"
                    onClick={() => props.setRefusing(true)}
                    label={getText("RefuseLabel")}
                />
            )}

            {(
                (!props.hasAccount && !props.representatives.length) ||
                    (props.hasAccount && props.resending) ||
                    (!props.hasAccount && props.createOwn) ||
                    (props.hasAccount && props.unlocking)
            )
                ? (
                    <ButtonControl
                        variant="brand"
                        onClick={handleInvite}
                        disabled={!props.validator.isValidToInvite}
                        label={getText("PortalInvitation_InviteLabel")}
                    />
                )
                : (
                    <ButtonControl
                        variant="brand"
                        onClick={handleSend}
                        disabled={!props.validator.isValidToSend}
                        label={getText("SendLabel")}
                    />
                )
            }
        </div>
    );
}

export { InvitationPopupFooter };
